import React from 'react';
import DayCell from './DayCell';

/**
 * CalendarGrid Component
 * Generates a 30-day grid from today forward
 * Supports week selection for bulk actions
 */
const CalendarGrid = ({ 
    startDate, 
    scheduledMeals = [], 
    onDayClick,
    selectedDate = null,
    onWeekSelect = null,
    selectedWeekStart = null
}) => {
    // Generate array of 30 days starting from startDate
    const days = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 30; i++) {
        const date = new Date(startDate);
        date.setDate(startDate.getDate() + i);
        date.setHours(0, 0, 0, 0);
        
        // Get meals scheduled for this date
        const mealsForDate = scheduledMeals.filter(meal => {
            const mealDate = new Date(meal.scheduled_date);
            mealDate.setHours(0, 0, 0, 0);
            return mealDate.getTime() === date.getTime();
        });

        days.push({
            date,
            meals: mealsForDate,
            isToday: date.getTime() === today.getTime(),
            isPast: date.getTime() < today.getTime()
        });
    }

    // Group days into weeks (7 days per week)
    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
        weeks.push(days.slice(i, i + 7));
    }

    // Check if a day is within the selected week
    const isDayInSelectedWeek = (date) => {
        if (!selectedWeekStart) return false;
        const weekEnd = new Date(selectedWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return date >= selectedWeekStart && date <= weekEnd;
    };

    // Get day names for header
    const dayNames = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];

    return (
        <div className="menu-scheduling-calendar">
            {/* Week day headers - with extra column for week selector */}
            <div className="grid grid-cols-8 mb-2">
                {/* Week selector header */}
                <div className="text-center font-mono text-xs uppercase tracking-wider text-neutral-900 py-2 bg-neutral-0 hairline opacity-60">
                    WK
                </div>
                {dayNames.map(dayName => (
                    <div 
                        key={dayName}
                        className="text-center font-mono text-xs uppercase tracking-wider text-neutral-900 py-2 bg-neutral-0 hairline"
                    >
                        {dayName}
                    </div>
                ))}
            </div>

            {/* Calendar grid with week selectors - no gaps between cells */}
            <div className="grid grid-cols-8">
                {weeks.map((week, weekIndex) => {
                    const weekStart = week[0].date;
                    const isWeekSelected = selectedWeekStart && 
                        selectedWeekStart.getTime() === weekStart.getTime();
                    
                    // Count meals in this week
                    const mealsInWeek = week.reduce((count, day) => count + day.meals.length, 0);
                    
                    return (
                        <React.Fragment key={weekIndex}>
                            {/* Week selector column */}
                            <div 
                                className={`flex flex-col items-center justify-center py-2 cursor-pointer transition-all hairline ${
                                    isWeekSelected 
                                        ? 'bg-[#1b263b] text-white' 
                                        : 'bg-neutral-0 text-neutral-900 hover:bg-neutral-50'
                                }`}
                                onClick={() => onWeekSelect && onWeekSelect(weekStart)}
                                title={`Select week ${weekIndex + 1} (${mealsInWeek} meals)`}
                            >
                                <span className="font-mono text-xs font-medium">
                                    {weekIndex + 1}
                                </span>
                                {mealsInWeek > 0 && (
                                    <span className={`font-mono text-xs ${
                                        isWeekSelected ? 'opacity-80' : 'opacity-60'
                                    }`}>
                                        {mealsInWeek}
                                    </span>
                                )}
                            </div>
                            
                            {/* Day cells */}
                            {week.map((day, dayIndex) => (
                                <DayCell
                                    key={`${day.date.getTime()}`}
                                    date={day.date}
                                    meals={day.meals}
                                    isToday={day.isToday}
                                    isPast={day.isPast}
                                    isSelected={selectedDate && selectedDate.getTime() === day.date.getTime()}
                                    isInSelectedWeek={isDayInSelectedWeek(day.date)}
                                    onClick={() => onDayClick(day.date)}
                                />
                            ))}
                            {/* Fill remaining cells if week is incomplete - no numbers in empty cells */}
                            {week.length < 7 && Array.from({ length: 7 - week.length }).map((_, i) => (
                                <div 
                                    key={`empty-${weekIndex}-${i}`} 
                                    className="aspect-square bg-neutral-0 hairline"
                                />
                            ))}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
};

export default CalendarGrid;

